// Gemini API (generativelanguage.googleapis.com) の薄いRESTクライアント
// 「Geminiによる要約と翻訳」拡張と同じ呼び出し方（x-goog-api-key ヘッダー + v1beta REST）

import {
  postJsonWithRetry,
  type ChatContent,
  type GenerateRequest,
  type GenerateResponse,
} from './llm.js'

// リファレンス拡張と同じくブロックを無効化（業務文言での誤ブロックを防ぐ）
const SAFETY_SETTINGS = [
  { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
  { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' },
]

function extractErrorMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: { message?: string } }).error
    return error?.message
  }
  return undefined
}

/** エラーレスポンスの google.rpc.RetryInfo（retryDelay: "32s" 形式）から待機時間を取り出す */
function extractRetryDelayMs(body: unknown): number | undefined {
  if (!body || typeof body !== 'object' || !('error' in body)) return undefined
  const details = (body as { error?: { details?: unknown } }).error?.details
  if (!Array.isArray(details)) return undefined
  for (const detail of details) {
    if (!detail || typeof detail !== 'object') continue
    const { '@type': type, retryDelay } = detail as { '@type'?: unknown; retryDelay?: unknown }
    if (typeof type !== 'string' || !type.endsWith('RetryInfo')) continue
    if (typeof retryDelay !== 'string') continue
    const seconds = Number.parseFloat(retryDelay)
    if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000
  }
  return undefined
}

/**
 * 中立フォーマットの履歴をGeminiのContent形式に整形する。
 * 他プロバイダ由来の思考ブロックや、Geminiが知らないフィールド
 * （functionCall.id 等）は送信前に取り除く（未知フィールドは400になるため）。
 */
function toGeminiContents(contents: ChatContent[]): unknown[] {
  return contents
    .map((content) => ({
      role: content.role,
      parts: content.parts
        .filter((part) => {
          const isForeignThought =
            (part.thought || part.redactedThinking !== undefined) &&
            part.thoughtProvider !== undefined &&
            part.thoughtProvider !== 'gemini'
          return !isForeignThought
        })
        .map((part) => {
          const out: Record<string, unknown> = {}
          if (part.text !== undefined) out['text'] = part.text
          if (part.thought !== undefined) out['thought'] = part.thought
          if (part.thoughtSignature !== undefined) out['thoughtSignature'] = part.thoughtSignature
          if (part.functionCall) {
            out['functionCall'] = { name: part.functionCall.name, args: part.functionCall.args }
          }
          if (part.functionResponse) {
            out['functionResponse'] = {
              name: part.functionResponse.name,
              response: part.functionResponse.response,
            }
          }
          return out
        })
        .filter((part) => Object.keys(part).length > 0),
    }))
    .filter((content) => content.parts.length > 0)
}

export async function generateGemini(request: GenerateRequest): Promise<GenerateResponse> {
  const { apiKey, model, contents, systemInstruction, functionDeclarations } = request

  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model.modelId)}:generateContent`

  const json = await postJsonWithRetry({
    provider: 'gemini',
    url,
    headers: { 'x-goog-api-key': apiKey },
    body: {
      contents: toGeminiContents(contents),
      systemInstruction: { parts: [{ text: systemInstruction }] },
      tools: [{ functionDeclarations }],
      safetySettings: SAFETY_SETTINGS,
      generationConfig: model.thinkingConfig ? { thinkingConfig: model.thinkingConfig } : {},
    },
    retryableStatuses: new Set([429, 503]),
    extractErrorMessage,
    extractRetryDelayMs,
    connectErrorMessage: 'Gemini APIに接続できませんでした。ネットワークを確認してください。',
  })

  return json as GenerateResponse
}
